import { GAMES, type GameId } from "@playpal/core";
import { useEffect, useState } from "react";
import { api, type GameStats, type LeaderboardRow, type OpponentRecord } from "../lib/api";
import type { Session } from "../lib/session";
import { Button, Card, Panel } from "./ui";

interface Props {
	session: Session;
	onBack: () => void;
	/** Start a new room to invite an old opponent to. */
	onInvite: (opponent: OpponentRecord) => void;
}

type Tab = "me" | "played" | "global";

export function ProfileScreen({ session, onBack, onInvite }: Props) {
	const [tab, setTab] = useState<Tab>("me");
	const [stats, setStats] = useState<GameStats[]>([]);
	const [opponents, setOpponents] = useState<OpponentRecord[]>([]);
	const [board, setBoard] = useState<LeaderboardRow[] | null>(null);
	const [loading, setLoading] = useState(true);
	// A failed fetch has to say so. Without these, a dead connection read as
	// "No games yet" to a child with a full record, and the top-players tab
	// said "Loading…" for ever with nothing that would ever try again.
	const [failed, setFailed] = useState(false);
	const [boardFailed, setBoardFailed] = useState(false);
	// Bumping these is how "try again" actually tries again: each is a
	// dependency of the fetch it re-runs.
	const [meAttempt, setMeAttempt] = useState(0);
	const [boardAttempt, setBoardAttempt] = useState(0);
	// Flips straight away rather than after the round-trip, so a tap feels like
	// it did something; it goes back if the save fails.
	const [optIn, setOptIn] = useState(session.profile?.globalOptIn ?? false);

	const token = session.token;

	// biome-ignore lint/correctness/useExhaustiveDependencies: meAttempt is the retry — bumping it is what makes a failed load run again.
	useEffect(() => {
		if (!token) return;
		let cancelled = false;
		setLoading(true);
		setFailed(false);
		void (async () => {
			try {
				const me = await api.me(token);
				if (cancelled) return;
				setStats(me.stats);
				setOpponents(me.opponents);
			} catch {
				if (!cancelled) setFailed(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [token, meAttempt]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: boardAttempt is the retry — bumping it is what makes a failed load run again.
	useEffect(() => {
		if (tab !== "global" || board !== null) return;
		let cancelled = false;
		setBoardFailed(false);
		api
			.leaderboard(token)
			.then((result) => {
				if (!cancelled) setBoard(result.rows);
			})
			.catch(() => {
				if (!cancelled) setBoardFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, [tab, board, token, boardAttempt]);

	const totals = sumStats(stats.filter((row) => row.mode === "friend"));
	const versusComputer = sumStats(stats.filter((row) => row.mode === "computer"));

	return (
		<Panel>
			<header className="mb-6 flex flex-wrap items-center justify-between gap-3">
				<Button tone="secondary" onClick={onBack}>
					← Back
				</Button>
				<Button tone="ghost" onClick={() => void session.signOut()}>
					Sign out
				</Button>
			</header>

			<Card className="mb-6 text-center">
				<p className="text-6xl" aria-hidden="true">
					{session.profile?.avatar}
				</p>
				<h1 className="text-3xl font-black">{session.profile?.name}</h1>
				<p className="mt-1 font-mono text-lg tracking-widest opacity-70">{session.profile?.code}</p>
			</Card>

			<div className="mb-5 flex gap-2">
				{(
					[
						["me", "My record"],
						["played", "Played"],
						["global", "Top players"],
					] as const
				).map(([value, label]) => (
					<button
						key={value}
						type="button"
						aria-pressed={tab === value}
						onClick={() => {
							setTab(value);
							// Tapping a tab whose load failed is the natural way of
							// asking again, so it is one.
							if (value === "global" && boardFailed) setBoardAttempt((made) => made + 1);
							if (value !== "global" && failed) setMeAttempt((made) => made + 1);
						}}
						className={`flex-1 rounded-2xl px-3 py-2 font-bold transition ${
							tab === value ? "bg-indigo-600 text-white shadow-lg" : "bg-white/60 dark:bg-white/10"
						}`}
					>
						{label}
					</button>
				))}
			</div>

			{loading && <Card>Loading your games…</Card>}

			{!loading && failed && tab !== "global" && (
				<LoadFailed
					what="Couldn't load your games."
					onRetry={() => setMeAttempt((made) => made + 1)}
				/>
			)}

			{!loading && !failed && tab === "me" && (
				<>
					<div className="mb-4 grid grid-cols-3 gap-3">
						<Tile label="Played" value={totals.played + versusComputer.played} />
						<Tile label="Won" value={totals.won + versusComputer.won} />
						<Tile label="Best streak" value={bestStreak(stats)} />
					</div>

					{stats.length === 0 ? (
						<Card className="text-center text-lg">
							No games yet. Play one and it'll show up here!
						</Card>
					) : (
						<Card>
							<h2 className="mb-3 text-xl font-bold">Game by game</h2>
							<ul className="space-y-2">
								{stats.map((row) => (
									<li
										key={`${row.gameId}-${row.mode}`}
										className="flex items-center justify-between rounded-2xl bg-white/60 px-4 py-3 dark:bg-white/10"
									>
										<span className="font-bold">
											{gameTitle(row.gameId)}{" "}
											<span className="text-sm font-semibold opacity-60">
												{row.mode === "computer" ? "vs computer" : "vs friends"}
											</span>
										</span>
										<span className="text-sm font-semibold">
											{row.won}W · {row.drawn}D · {row.lost}L
											{row.bestStreak > 1 && ` · best run ${row.bestStreak}`}
										</span>
									</li>
								))}
							</ul>
						</Card>
					)}

					<Card className="mt-4">
						<label className="flex items-center justify-between gap-4">
							<span>
								<span className="block text-lg font-bold">Show me on the top players list</span>
								<span className="block text-sm opacity-70">
									Only your made-up name, animal and wins. Off by default.
								</span>
							</span>
							<input
								type="checkbox"
								checked={optIn}
								onChange={(event) => {
									const next = event.target.checked;
									setOptIn(next);
									setBoard(null);
									void session.update({ globalOptIn: next }).catch(() => setOptIn(!next));
								}}
								className="h-7 w-7 accent-indigo-600"
							/>
						</label>
					</Card>
				</>
			)}

			{!loading && !failed && tab === "played" && (
				<Card>
					<h2 className="mb-1 text-xl font-bold">Everyone you've played</h2>
					<p className="mb-4 text-sm opacity-70">
						Head-to-head records for the friends who also have a player card. A record is forgotten
						after 90 days with no game between you — playing again starts the 90 days over.
					</p>
					{opponents.length === 0 ? (
						<p className="text-lg">Nobody yet — play a friend who has a card and they'll appear.</p>
					) : (
						<ul className="space-y-2">
							{opponents.map((opponent) => (
								<li
									key={opponent.code}
									className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/60 px-4 py-3 dark:bg-white/10"
								>
									<span className="flex items-center gap-3">
										<span className="text-3xl" aria-hidden="true">
											{opponent.avatar}
										</span>
										<span>
											<span className="block font-bold">{opponent.name}</span>
											<span className="block text-sm opacity-70">
												{opponent.won}W · {opponent.drawn}D · {opponent.lost}L
											</span>
										</span>
									</span>
									<Button tone="secondary" onClick={() => onInvite(opponent)}>
										Play again
									</Button>
								</li>
							))}
						</ul>
					)}
				</Card>
			)}

			{tab === "global" && (
				<Card>
					<h2 className="mb-1 text-xl font-bold">Top players</h2>
					<p className="mb-4 text-sm opacity-70">
						Only players who chose to appear here, with at least three games.
					</p>
					{boardFailed ? (
						<LoadFailed
							what="Couldn't load the top players."
							onRetry={() => setBoardAttempt((made) => made + 1)}
							bare
						/>
					) : board === null ? (
						<p>Loading…</p>
					) : board.length === 0 ? (
						<p className="text-lg">Nobody has joined the board yet. You could be first!</p>
					) : (
						<ol className="space-y-2">
							{board.map((row) => (
								<li
									key={row.rank}
									className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${
										row.you ? "bg-indigo-600/90 text-white" : "bg-white/60 dark:bg-white/10"
									}`}
								>
									<span className="w-8 text-xl font-black">{row.rank}</span>
									<span className="text-2xl" aria-hidden="true">
										{row.avatar}
									</span>
									<span className="flex-1 font-bold">{row.name}</span>
									<span className="text-sm font-semibold">
										{row.won} wins · {row.winRate}%
									</span>
								</li>
							))}
						</ol>
					)}
				</Card>
			)}

			{token && <DeleteCard token={token} onDeleted={() => void session.signOut()} />}
		</Panel>
	);
}

/**
 * A fetch that failed says so, in the app's own words, with a way to try
 * again — never a "Loading…" that no longer is, and never an empty record
 * that is really a dead connection.
 */
function LoadFailed({
	what,
	onRetry,
	bare = false,
}: {
	what: string;
	onRetry: () => void;
	/** Already sitting inside a Card, so it should not bring its own. */
	bare?: boolean;
}) {
	const body = (
		<>
			<p className="text-lg font-semibold">{what} Check the internet and try again.</p>
			<Button className="mt-3" tone="secondary" onClick={onRetry}>
				🔁 Try again
			</Button>
		</>
	);
	return bare ? (
		<div className="text-center">{body}</div>
	) : (
		<Card className="text-center">{body}</Card>
	);
}

function Tile({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-2xl bg-white/70 px-4 py-4 text-center shadow-md dark:bg-white/10">
			<p className="text-3xl font-black">{value}</p>
			<p className="text-sm font-semibold opacity-70">{label}</p>
		</div>
	);
}

function gameTitle(gameId: GameId | "all"): string {
	return gameId === "all" ? "All games" : (GAMES[gameId]?.title ?? gameId);
}

function sumStats(rows: GameStats[]) {
	return rows.reduce(
		(total, row) => ({
			played: total.played + row.played,
			won: total.won + row.won,
		}),
		{ played: 0, won: 0 },
	);
}

function bestStreak(rows: GameStats[]): number {
	return rows.reduce((best, row) => Math.max(best, row.bestStreak), 0);
}

/**
 * Deleting is deliberately two taps and plainly worded. It is the one control
 * here that cannot be undone — there is no soft delete on the server — and the
 * person using it may well be a parent rather than the child.
 */
function DeleteCard({ token, onDeleted }: { token: string; onDeleted: () => void }) {
	const [asking, setAsking] = useState(false);
	const [busy, setBusy] = useState(false);

	if (!asking) {
		return (
			<div className="mt-8 text-center">
				<Button tone="ghost" onClick={() => setAsking(true)}>
					Delete my player card
				</Button>
			</div>
		);
	}

	return (
		<Card className="mt-8 text-center">
			<p className="font-bold">Delete this player card?</p>
			<p className="mt-1 text-sm opacity-80">
				Your name, your record and everyone you've played will be removed. This can't be undone.
			</p>
			<div className="mt-4 flex justify-center gap-3">
				<Button tone="secondary" onClick={() => setAsking(false)} disabled={busy}>
					Keep it
				</Button>
				<Button
					onClick={() => {
						setBusy(true);
						void api
							.deleteMe(token)
							.then(onDeleted)
							.finally(() => setBusy(false));
					}}
					disabled={busy}
				>
					{busy ? "Deleting…" : "Yes, delete it"}
				</Button>
			</div>
		</Card>
	);
}
